import { useTranslation } from 'react-i18next';
import { ORDER_METHODS } from '../../services/exports/Constants';
import AddressHelpers from '../../services/helpers/AddressHelpers';
import moment from 'moment';
import useCompanyLocation from '../../hooks/location/useCompanyLocation';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import { useMemo } from 'react';
import collect from 'collect.js';

export default function OrderMethodInfo() {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Checkout:OrderMethodInfo' });

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const { address } = useCompanyLocation(cached_order.scheduled_for);
  const { formatDateTime, createScheduledOrderSlot } = useAppMethods();

  const timeDetails = useMemo(() => {
    if (!cached_order.scheduled_for) {
      return t('time_labels.asap');
    }

    return t('time_labels.scheduled', {
      date: moment(cached_order.scheduled_for).isSame(moment(), 'day')
        ? t('time_labels.today')
        : formatDateTime(cached_order.scheduled_for, { format: 'dddd, DD MMM' }),
      time: createScheduledOrderSlot(cached_order.scheduled_for),
    });
  }, [cached_order.scheduled_for]);

  const locationDetails = useMemo(
    () =>
      ({
        [ORDER_METHODS.PICKUP]: `${company?.name}, ${address}`,
        [ORDER_METHODS.DELIVERY]: AddressHelpers.setCountry(company?.country).format({
          street_name: cached_order.delivery_street_name,
          street_number: cached_order.delivery_street_number,
        }),
        [ORDER_METHODS.ROOM_SERVICE]: collect([
          cached_order.room_service_location?.name,
          AddressHelpers.setCountry(company?.country).format({
            street_name: cached_order.room_service_location?.street_name,
            street_number: cached_order.room_service_location?.street_number,
          }),
          cached_order.room_service_details
            ? `${t(`${cached_order.room_service_location?.type}.details_label`)}: ${cached_order.room_service_details}`
            : null,
        ])
          .whereNotNull()
          .implode(', '),
      })[cached_order.method],
    [
      address,
      company?.name,
      company?.country,
      cached_order.method,
      cached_order.room_service_location,
      cached_order.room_service_details,
      cached_order.delivery_street_name,
      cached_order.delivery_street_number,
    ],
  );

  return (
    <>
      <p className="text-sm">{timeDetails}</p>
      <p className="text-sm">{locationDetails}</p>
    </>
  );
}
